import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { parentAPI } from '../../lib/api';
import {
  CheckCircle, AlertCircle,
  Play, Check, X, Clock, Sparkles, RefreshCw, Archive
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Parent Week View - 7-day calendar strip + tasks + activities
 */
export const ParentWeek = () => {
  const [searchParams] = useSearchParams();
  const urlFamilyId = searchParams.get('family_id');
  const familyId = (urlFamilyId && urlFamilyId !== 'null') 
    ? urlFamilyId 
    : localStorage.getItem('family_id');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activityStatus, setActivityStatus] = useState({}); // Local activity tracking
  const [completingTasks, setCompletingTasks] = useState(new Set());

  const STALE_DAYS = 14;
  const isTaskStale = (task) => {
    const now = new Date();
    const ref = task.due_date ? new Date(task.due_date) : task.created_at ? new Date(task.created_at) : null;
    return ref && (now - ref) / 86400000 > STALE_DAYS;
  };

  const handleTaskAction = async (taskId, action) => {
    setCompletingTasks(prev => new Set([...prev, taskId]));
    try {
      const status = action === 'done' ? 'done' : 'cancelled';
      await parentAPI.updateTaskStatus(taskId, status);
      setData(prev => ({
        ...prev,
        tasks_due: prev.tasks_due.filter(t => t.id !== taskId)
      }));
      if (action === 'done') toast.success('Task marked as done!');
      else toast.info('Task archived');
    } catch {
      toast.error('Could not update task. Please try again.');
    } finally {
      setCompletingTasks(prev => { const n = new Set(prev); n.delete(taskId); return n; });
    }
  };

  useEffect(() => {
    if (familyId) {
      loadWeekData();
      // Load activity status from localStorage
      const saved = localStorage.getItem(`kivo_activities_${familyId}`);
      if (saved) {
        setActivityStatus(JSON.parse(saved));
      }
    }
  }, [familyId]);

  const loadWeekData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await parentAPI.getWeek(familyId, 7);
      if (response.data?.ok) {
        setData(response.data);
      } else {
        setError(response.data?.error || 'Failed to load week view');
      }
    } catch (err) {
      console.error('Failed to load week data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityAction = (activityId, action) => {
    const newStatus = { ...activityStatus };
    newStatus[activityId] = action; // 'done', 'skipped', 'added'
    setActivityStatus(newStatus);
    localStorage.setItem(`kivo_activities_${familyId}`, JSON.stringify(newStatus));
    
    if (action === 'done') toast.success('Activity marked as done!');
    if (action === 'added') toast.success('Added to checklist');
    if (action === 'skipped') toast.info('Activity skipped');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (!familyId) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">No Family Selected</h2>
        <Link to="/login" className="text-primary hover:underline">Please log in</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="week-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-slate-500">Loading week view...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-100 text-red-700 px-6 py-4 rounded-xl flex items-center justify-between">
        <span>{error}</span>
        <button
          onClick={loadWeekData}
          className="ml-4 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  const { calendar, tasks_due, activities_suggested } = data || {};

  const CATEGORY_COLORS = {
    class: 'bg-purple-400',
    sport: 'bg-orange-400',
    art: 'bg-pink-400',
    music: 'bg-indigo-400',
    activity: 'bg-primary',
  };

  return (
    <div data-testid="parent-week-page" className="space-y-5 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-2xl font-bold text-slate-900 tracking-tight">This Week</h1>
        <p className="text-slate-500 text-base md:text-sm mt-1">Plan ahead and stay organized</p>
      </div>

      {/* 7-Day Calendar Strip */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 md:p-6" data-testid="week-calendar">
        <h2 className="text-sm font-medium text-slate-500 mb-3">Week overview</h2>
        
        <div className="grid grid-cols-7 gap-1.5 md:gap-2">
          {calendar?.map((day, idx) => {
            const isWeekday = !['Sat', 'Sun'].includes(day.day_name);
            const isSchoolDay = isWeekday && !day.is_holiday && !day.is_today;
            return (
            <div
              key={day.date}
              className={`relative p-2 md:p-3 rounded-xl text-center transition-all ${
                day.is_today
                  ? 'bg-primary text-white shadow-md'
                  : day.is_holiday
                    ? 'bg-amber-50 border-2 border-amber-200'
                    : isSchoolDay
                      ? 'bg-blue-50 border border-blue-100 hover:bg-blue-100'
                      : 'bg-slate-50 hover:bg-slate-100'
              }`}
            >
              <p className={`text-xs font-medium ${
                day.is_today ? 'text-primary-50' : day.is_holiday ? 'text-amber-600' : isSchoolDay ? 'text-blue-400' : 'text-slate-400'
              }`}>
                {day.day_name}
              </p>
              <p className={`text-lg font-bold ${
                day.is_today ? 'text-white' : day.is_holiday ? 'text-amber-700' : isSchoolDay ? 'text-blue-700' : 'text-slate-800'
              }`}>
                {day.day_num}
              </p>
              {day.is_holiday && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center">
                  <span className="text-[8px] text-amber-900">H</span>
                </div>
              )}
              {isSchoolDay && (
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mx-auto mt-1" />
              )}
              {day.event_name && (
                <p className={`text-[10px] mt-1 truncate ${
                  day.is_today ? 'text-primary-50' : 'text-slate-500'
                }`} title={day.event_name}>
                  {day.event_name.length > 12 ? `${day.event_name.slice(0, 12)}...` : day.event_name}
                </p>
              )}
              {day.email_events?.length > 0 && (
                <p className={`text-[10px] mt-0.5 truncate ${
                  day.is_today ? 'text-teal-200' : 'text-teal-500'
                }`} title={day.email_events.map(e => e.title).join(', ')}>
                  {day.email_events[0].title.length > 12
                    ? `${day.email_events[0].title.slice(0, 12)}...`
                    : day.email_events[0].title}
                </p>
              )}
              {/* Routine dots */}
              {day.routines?.length > 0 && (
                <div className="flex items-center justify-center gap-0.5 mt-1" title={day.routines.map(r => r.name).join(', ')}>
                  {day.routines.slice(0, 3).map((r, i) => (
                    <div
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full ${
                        day.is_today ? 'bg-white/70' : (CATEGORY_COLORS[r.category] || 'bg-primary-100')
                      }`}
                    />
                  ))}
                  {day.routines.length > 3 && (
                    <span className={`text-[8px] ${day.is_today ? 'text-white/70' : 'text-slate-400'}`}>+{day.routines.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          );})}
        </div>

        {/* Legend */}
        <div className="flex items-center flex-wrap gap-4 mt-4 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-primary"></div>
            <span>Today</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-100 border border-blue-200"></div>
            <span>School day</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-200 border border-amber-300"></div>
            <span>Holiday</span>
          </div>
          {calendar?.some(d => d.routines?.length > 0) && (
            <div className="flex items-center gap-1">
              <div className="flex gap-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
              </div>
              <span>Routines</span>
            </div>
          )}
          {calendar?.some(d => d.email_events?.length > 0) && (
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-teal-100 border border-teal-200"></div>
              <span>School event</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Upcoming Tasks */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 md:p-6" data-testid="week-tasks">
          <h2 className="text-sm font-medium text-slate-500 mb-3">
            Pending tasks
          </h2>
          
          {tasks_due?.length > 0 ? (
            <div className="space-y-2">
              {tasks_due.map((task) => {
                const stale = isTaskStale(task);
                const busy = completingTasks.has(task.id);
                return (
                  <div
                    key={task.id}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${stale ? 'bg-amber-50 border border-amber-100' : 'bg-slate-50 hover:bg-slate-100'}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      task.priority === 'high' ? 'bg-red-100' : 'bg-primary-100'
                    }`}>
                      <CheckCircle className={`w-4 h-4 ${task.priority === 'high' ? 'text-red-600' : 'text-primary'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{task.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-slate-400">
                          {task.due_date ? `Due ${formatDate(task.due_date)}` : 'No due date'}
                        </p>
                        {stale && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                            May be outdated
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        disabled={busy}
                        onClick={() => handleTaskAction(task.id, 'done')}
                        className="flex items-center gap-1 px-2 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary rounded-lg text-xs font-medium transition-colors disabled:opacity-40"
                        title="Mark done"
                      >
                        <Check className="w-3 h-3" />
                        Done
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => handleTaskAction(task.id, 'archive')}
                        className="p-1.5 hover:bg-slate-200 text-slate-400 rounded-lg transition-colors disabled:opacity-40"
                        title="Archive task"
                      >
                        <Archive className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-6 bg-green-50 rounded-xl text-center">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-green-700 font-medium">All clear this week!</p>
              <p className="text-sm text-green-600 mt-1">No pending tasks</p>
            </div>
          )}
        </div>

        {/* Suggested Activities */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 md:p-6" data-testid="week-activities">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-slate-500">
              Weekly activities
            </h2>
            <span className="text-xs text-slate-400">Suggestions for this week</span>
          </div>
          
          {activities_suggested?.length > 0 ? (
            <div className="space-y-3">
              {activities_suggested.map((activity) => {
                const status = activityStatus[activity.id];
                const isDone = status === 'done';
                const isSkipped = status === 'skipped';
                const isAdded = status === 'added';
                
                return (
                  <div 
                    key={activity.id} 
                    className={`p-4 rounded-xl transition-all ${
                      isDone ? 'bg-green-50 border border-green-100' :
                      isSkipped ? 'bg-slate-100 opacity-60' :
                      isAdded ? 'bg-primary-50 border border-primary-100' :
                      'bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <Sparkles className={`w-4 h-4 flex-shrink-0 ${
                          isDone ? 'text-green-600' :
                          isAdded ? 'text-primary' :
                          'text-amber-500'
                        }`} />
                        <p className={`font-medium ${
                          isDone ? 'text-green-800 line-through' :
                          isSkipped ? 'text-slate-500 line-through' :
                          'text-slate-800'
                        }`}>
                          {activity.name}
                        </p>
                        {isDone && (
                          <span className="ml-auto px-2 py-0.5 bg-green-200 text-green-800 rounded text-xs font-medium">
                            Done!
                          </span>
                        )}
                        {isAdded && (
                          <span className="ml-auto px-2 py-0.5 bg-primary-100 text-primary rounded text-xs font-medium">
                            Added
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1.5">{activity.description}</p>
                      <div className="flex items-center justify-between mt-2.5">
                        <div className="flex items-center gap-3 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {activity.duration_min} min
                          </span>
                          <span className="capitalize">{activity.category}</span>
                        </div>
                        {!status && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleActivityAction(activity.id, 'added')}
                              className="flex items-center gap-1 px-2.5 py-1.5 hover:bg-primary-100 rounded-lg transition-colors text-xs text-primary font-medium"
                              title="Add to checklist"
                            >
                              <Play className="w-3.5 h-3.5" />
                              Add
                            </button>
                            <button
                              onClick={() => handleActivityAction(activity.id, 'done')}
                              className="flex items-center gap-1 px-2.5 py-1.5 hover:bg-green-100 rounded-lg transition-colors text-xs text-green-700 font-medium"
                              title="Mark done"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Done
                            </button>
                            <button
                              onClick={() => handleActivityAction(activity.id, 'skipped')}
                              className="flex items-center gap-1 px-2.5 py-1.5 hover:bg-slate-200 rounded-lg transition-colors text-xs text-slate-500 font-medium"
                              title="Skip"
                            >
                              <X className="w-3.5 h-3.5" />
                              Skip
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-6 bg-slate-50 rounded-xl text-center">
              <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">Activity suggestions coming soon</p>
            </div>
          )}
          
          {/* TODO: Persist activity checklist to backend when activity_checklist table schema is confirmed */}
        </div>
      </div>
    </div>
  );
};

export default ParentWeek;
